import {
  memo,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Button,
  Card,
  CardBody,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from '@nextui-org/react';
import { exportTableToExcel } from '../../utils/export-to-excel';
import { Settlement } from 'common/src/utils/types';
import {
  ChartFilters,
  processChartData,
  groupAndAggregateData,
} from '../../utils/functions';
import LineChart from '../../../../common/src/components/charts/LineChart';

import { generateChartTooltip } from './settlements-summary-tooltip';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import {
  UNIFIED_TABLE_CLASSNAMES,
  TableExportRef,
} from '../../utils/table-styles';

interface Props {
  settlements: Settlement[];
  chartViewMode: 'day' | 'week';
  chartOrTable: 'chart' | 'table';
  dateType: 'eta' | 'etd' | 'sale_date';
  filters: ChartFilters;
  valueMode: 'average' | 'total';
  multiplier: number;
}

const prepareChartData = (
  settlements: Settlement[],
  filters: ChartFilters,
  viewMode: 'day' | 'week',
  valueMode: 'average' | 'total',
  multiplier: number
) => {
  if (filters.format.length === 0 || filters.format.includes('all')) {
    return { series: [], categories: [] };
  }

  const groups = groupAndAggregateData(
    settlements,
    filters,
    'receiver',
    viewMode
  );
  return processChartData(groups, viewMode, undefined, valueMode, multiplier);
};

const AccumulatedFobChart = memo(
  forwardRef<TableExportRef, Props>(
    (
      {
        settlements,
        chartViewMode,
        chartOrTable,
        dateType,
        filters,
        valueMode,
        multiplier,
      },
      ref
    ) => {
      const { t } = useTranslation('settlements');

      const chartData = useMemo(
        () =>
          prepareChartData(
            settlements,
            { ...filters, dateType },
            chartViewMode,
            valueMode,
            multiplier
          ),
        [settlements, filters, chartViewMode, dateType, valueMode, multiplier]
      );

      const [visibleCount, setVisibleCount] = useState(10);

      // Rank series by total FOB descending — always, regardless of valueMode.
      // Using the raw totalFob carried on each data point (not y, which switches
      // between totalFob and weighted average depending on the selected view).
      const sortedSeries = useMemo(() => {
        return [...chartData.series].sort((a, b) => {
          const aFob = a.data.reduce((s, p: any) => s + (p?.totalFob || 0), 0);
          const bFob = b.data.reduce((s, p: any) => s + (p?.totalFob || 0), 0);
          return bFob - aFob;
        });
      }, [chartData.series]);

      const visibleSeries = useMemo(
        () => sortedSeries.slice(0, visibleCount),
        [sortedSeries, visibleCount]
      );

      // Per-date total across ALL series (not paginated).
      // - 'total' mode:   sum of totalFob per column.
      // - 'average' mode: weighted average = ΣtotalFob / ΣtotalKilos × multiplier.
      //   (Plain sum of averages would be meaningless.)
      const categoryTotals = useMemo(
        () =>
          chartData.categories.map((_, i) => {
            let sumFob = 0;
            let sumKilos = 0;
            sortedSeries.forEach((s) => {
              const p: any = s.data[i];
              sumFob += p?.totalFob || 0;
              sumKilos += p?.totalKilos || 0;
            });
            if (valueMode === 'total') return sumFob;
            return sumKilos > 0 ? (sumFob / sumKilos) * multiplier : 0;
          }),
        [chartData.categories, sortedSeries, valueMode, multiplier]
      );

      const hasMore = sortedSeries.length > visibleCount;
      const loadMore = () =>
        setVisibleCount((c) => Math.min(c + 10, sortedSeries.length));

      // Total mode = whole-dollar amounts (no decimals); Promedio mode =
      // per-unit price where cents matter, so keep 2 decimals.
      const formatCell = (v: number) =>
        valueMode === 'total'
          ? `$${Math.round(v).toLocaleString('en-US')}`
          : `$${v.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;

      // Excel export: emit a real number with a currency format so Excel treats
      // the cell as numeric (SUM/AVG work) while still displaying it formatted.
      const excelCell = (v: number) => ({
        v: valueMode === 'total' ? Math.round(v) : Number(v.toFixed(2)),
        z: valueMode === 'total' ? '"$"#,##0' : '"$"#,##0.00',
        t: 'n' as const,
      });

      const tooltipCustom = (props: any) =>
        generateChartTooltip({
          ...props,
          chartViewMode,
          t,
        });

      const handleExport = () => {
        // Export always covers the full (sorted) series, not just the visible
        // slice — "Cargar más" is a UI pagination, not an export scope.
        exportTableToExcel({
          fileName: 'FOB_Evolutivo',
          sheetName: 'Evolutivo',
          headers: [t('CLIENTE'), ...chartData.categories],
          rows: [
            [
              t('Total'),
              ...categoryTotals.map((v) => (v ? excelCell(v) : '—')),
            ],
            ...sortedSeries.map((serie) => [
              serie.name,
              ...chartData.categories.map((_, index) =>
                serie.data[index]?.y != null
                  ? excelCell(serie.data[index].y as number)
                  : '—'
              ),
            ]),
          ],
        });
      };

      useImperativeHandle(ref, () => ({ exportToExcel: handleExport }), [
        handleExport,
      ]);

      const xaxisTitle = useMemo(() => {
        const timeLabel = chartViewMode === 'day' ? t('Día') : t('Semana');
        const dateLabel =
          dateType === 'eta'
            ? t('ETA')
            : dateType === 'etd'
              ? t('ETD')
              : t('Fecha de Venta');
        return `${timeLabel} (${dateLabel})`;
      }, [chartViewMode, dateType, t]);

      return (
        <Card className='w-full'>
          <CardBody className={chartOrTable === 'chart' ? '' : 'p-0'}>
            {chartOrTable === 'chart' ? (
              <LineChart
                key={`client-chart-${chartViewMode}`}
                series={visibleSeries}
                xaxisCategories={chartData.categories}
                xaxisTitle={xaxisTitle}
                yaxisTitle={t('FOB Acumulado (USD)')}
                tooltipCustom={tooltipCustom}
                selectedCurrency='USD'
                priceView='kg'
                height={500}
                legendPosition='bottom'
                viewMode={chartViewMode}
                minYAxisValue={
                  Math.min(
                    ...visibleSeries.map((serie) =>
                      Math.min(...serie.data.map((point) => point.y))
                    )
                  ) || 0
                }
              />
            ) : (
              <Table
                key={`fob-table-${chartViewMode}-${dateType}`}
                isHeaderSticky
                classNames={{
                  ...UNIFIED_TABLE_CLASSNAMES,
                  base: 'max-h-[600px] overflow-auto',
                }}
                aria-label={t('FOB Acumulado por Cliente')}
              >
                <TableHeader>
                  <TableColumn>{t('CLIENTE')}</TableColumn>
                  {chartData.categories.map((category) => (
                    <TableColumn key={category}>{category}</TableColumn>
                  ))}
                </TableHeader>
                <TableBody>
                  <TableRow
                    key='__totals__'
                    className='bg-default-100 dark:bg-default-50 font-semibold'
                  >
                    <TableCell className='font-semibold'>
                      {t('Total')}
                    </TableCell>
                    {categoryTotals.map((total, i) => (
                      <TableCell key={`total-${i}`} className='font-semibold'>
                        {total ? formatCell(total) : '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                  {visibleSeries.map((serie) => (
                    <TableRow key={serie.name}>
                      <TableCell className='font-medium'>
                        {serie.name}
                      </TableCell>
                      {chartData.categories.map((category, index) => (
                        <TableCell
                          key={`${serie.name}-${category}`}
                          className=''
                        >
                          {serie.data[index]?.y
                            ? formatCell(serie.data[index].y as number)
                            : '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {hasMore && (
              <div className='flex justify-center mt-2'>
                <Button
                  variant='light'
                  size='sm'
                  onPress={loadMore}
                  className='w-full'
                >
                  {t('Cargar más')} ({sortedSeries.length - visibleCount}{' '}
                  {t('restantes')})
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      );
    }
  )
);

export default AccumulatedFobChart;
